import React, { PureComponent } from "react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import FullCalendar from "@fullcalendar/react";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import styled, {
  ThemeProvider as StyledThemeProvider
} from "styled-components";
import { createRoot } from "react-dom/client";
import moment from "moment";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { StylesProvider, jssPreset } from "@material-ui/core/styles";
import { create } from "jss";
import { Dialog, DialogContent } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import MultipleMonthCalendar from "./MultipleMonthCalendar";
import { theme as StyledTheme } from "../../assets/theme/StyledTheme";
import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import { getDemoScheduleSlotStatus } from "../../demo/demoScheduleSlotStatus";

import "@fullcalendar/core/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";

function localClockAsUtc() {
  const local = moment();
  return moment
    .utc({
      year: local.year(),
      month: local.month(),
      date: local.date(),
      hour: local.hour(),
      minute: local.minute(),
      second: local.second()
    })
    .toDate();
}

/** Fecha listener de clique fora do submenu "Mais ações" (cards do horário). */
const accelMoreOutsideHandlers = new WeakMap();

function removeAccelMoreOutsideHandler(wrapEl) {
  const h = accelMoreOutsideHandlers.get(wrapEl);
  if (h) {
    document.removeEventListener("click", h, true);
    accelMoreOutsideHandlers.delete(wrapEl);
  }
}

/** Converte #RGB / #RRGGBB em rgba (fallback: teal escuro). */
function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string") {
    return `rgba(26, 58, 69, ${alpha})`;
  }
  let h = hex.trim();
  if (h[0] === "#") h = h.slice(1);
  if (h.length === 3) {
    h = h
      .split("")
      .map(c => c + c)
      .join("");
  }
  if (h.length !== 6) {
    return `rgba(26, 58, 69, ${alpha})`;
  }
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return `rgba(26, 58, 69, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Borda + fundo alinhados ao tema do card; “realizado” mantém ênfase verde. */
function styleAccelButton(node, accent, variant) {
  if (variant === "realizado") {
    Object.assign(node.style, {
      border: "1px solid rgba(46, 125, 50, 0.55)",
      backgroundColor: "rgba(46, 125, 50, 0.07)"
    });
  } else {
    Object.assign(node.style, {
      border: `1px solid ${hexToRgba(accent, 0.42)}`,
      backgroundColor: hexToRgba(accent, 0.09)
    });
  }
}

const CalendarStyle = styled.div`
  box-sizing: border-box;
  width: 100%;
  ${props =>
    props.$fillContainer
      ? `
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    align-self: stretch;
    min-height: 0;
    height: 100%;
  `
      : `
    display: block;
  `}
  .fc-toolbar,
  .fc-view-container,
  .month-calendar-container {
    background-color: white;
  }
  #month-calendar-container {
    height: 0;
    overflow: hidden;
    width: 100%;
  }
  .fc-view-container {
    margin-top: ${({ today }) => (today ? 0 : 22)}px;
    &.today-container {
      margin-top: 0;
    }
  }
  .fc-toolbar {
    margin: 0;
    padding: 25px 40px;
    height: 96px;
    border-bottom: 1px solid #f2f2f2;

    h2 {
      display: flex;
      align-items: center;
      font-family: "Roboto", sans-serif;
      font-size: 26px;
      font-weight: 300;
      color: #000000;
    }
    .fc-left {
      display: flex;
      flex-direction: row-reverse;
      button {
        color: black;
        background-color: transparent;
        border: none;
        box-shadow: none;
        &.fc-prevButton-button,
        &.fc-nextButton-button {
          display: flex;
          align-items: center;
        }
      }
    }
  }
  .fc-time-grid {
    .fc-slats td {
      height: 60px;
    }
    .fc-time-grid-event {
      margin-bottom: 1px;
    }
  }
  .fc-time-grid .fc-time-grid-event.fc-event {
    overflow: hidden;
  }
  .fc-event {
    border-radius: 0;
    border: none;
    /* Afasta o card do gutter de horários (evita encostar em "14:00" etc.). */
    left: 8px !important;
    right: 0 !important;

    &.min-10,
    &.min-20,
    &.min-30 {
      .fc-content {
        .fc-title {
          h1,
          h2 {
            font-size: 11px;
            margin: 0;
          }
        }
      }
    }

    &.min-10,
    &.min-20 {
      .fc-content {
        .fc-title {
          display: flex;
          align-items: center;
          overflow: hidden;
          white-space: nowrap;
          .fc-event-title-text h1 {
            display: inline-block;
          }
          .fc-event-title-text h2 {
            display: inline-block;
            margin-left: 6px;
          }
          h1 {
            display: inline-block;
          }
          h2 {
            display: inline-block;
            margin-left: 6px;
          }
        }
      }
    }

    &.min-10 {
      .fc-content {
        padding: 0 12px;
        .fc-time {
          display: none;
        }
        .fc-title {
          height: 100%;
        }
      }
    }

    &.min-20,
    &.min-30 {
      .fc-content {
        padding: 0 14px;
        .fc-time span {
          font-size: 11px;
        }
      }
    }

    &.min-45,
    &.min-long {
      .fc-content {
        padding: 6px 12px;
        h1 {
          font-size: 14px;
          margin-top: 5px;
        }
        h2 {
          margin-top: 5px;
        }
      }
    }
  }

  .fc-time {
    font-family: "Roboto", sans-serif;
    min-height: 20px;
    font-size: 16px;
    font-weight: 300;
    span {
      white-space: normal;
    }
    @media screen and (max-width: 1024px) {
      font-size: 13px;
    }
  }

  .fc-title h1 {
    opacity: 0.88;
    min-height: 16px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-family: "Roboto", sans-serif;
    font-size: 16px;
    @media screen and (max-width: 1024px) {
      font-size: 13px;
    }
    margin: 5px 0;
  }
  .fc-title h2 {
    opacity: 0.78;
    min-height: 16px;
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media screen and (max-width: 1024px) {
      font-size: 13px;
    }
    font-weight: 300;
  }
  /* Coluna estreita na Home: permite quebra e evita texto “encavalado” */
  .fc-time-grid .fc-event .fc-title {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    width: 100%;
    text-align: left;
    direction: ltr;
  }
  .fc-event-title-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 8px;
    width: 100%;
    min-width: 0;
    flex-shrink: 0;
    text-align: left;
    direction: ltr;
  }
  .fc-event-icon.material-icons {
    font-size: 22px;
    line-height: 1.15;
    flex-shrink: 0;
    margin-top: 2px;
    min-width: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .fc-event-title-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    text-align: left;
  }
  /* Preferir quebra em espaços; evita “Matemá|tica” por word-break agressivo */
  .fc-time-grid .fc-event .fc-title h1,
  .fc-time-grid .fc-event .fc-title h2,
  .fc-time-grid .fc-event .fc-title .fc-event-title-text h1,
  .fc-time-grid .fc-event .fc-title .fc-event-title-text h2 {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    max-width: 100%;
    word-break: normal;
    overflow-wrap: break-word;
    hyphens: none;
    margin: 0;
    text-align: left;
  }
  .fc-time-grid .fc-event.min-10 .fc-event-icon.material-icons,
  .fc-time-grid .fc-event.min-20 .fc-event-icon.material-icons,
  .fc-time-grid .fc-event.min-30 .fc-event-icon.material-icons {
    font-size: 14px;
    margin-top: 0;
  }
  .fc-content {
    height: 100%;
    padding: 12px;
    position: relative;
    z-index: 2;
    text-align: left;
    direction: ltr;
    @media screen and (max-width: 1024px) {
      padding: 10px 10px 10px 12px;
    }
  }
  /* FullCalendar default: .fc-time + .fc-title em row. Forçar coluna (inclui eventos longos sem .min-*). */
  .fc-time-grid .fc-time-grid-event .fc-content {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
    text-align: left !important;
    box-sizing: border-box !important;
  }
  .fc-time-grid .fc-time-grid-event .fc-content .fc-time {
    flex: 0 0 auto !important;
    align-self: flex-start !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .fc-time-grid .fc-time-grid-event .fc-content .fc-title {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    align-self: stretch !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .fc-bg {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;
    opacity: 0.88;
    z-index: 0;
    /* Borda accent vem do eventRender (border-left no mesmo div — evita 2ª div sobre a grade) */
  }
  .fc-now-indicator {
    z-index: 4;
    border-color: #e53935;
  }
  .fc-axis.fc-time {
    opacity: 0.5;
    font-family: "Roboto", sans-serif;
    font-size: 16px;
    color: #000000;
    text-align: center;
  }
  .fc-time-grid .fc-slats .fc-minor td {
    border-top-style: dashed;
  }
  .fc-day-header {
    text-align: center;
    border-width: 0;
    padding: 18px 0;
    span {
      font-family: "Roboto", sans-serif;
      font-size: 21px;
      font-weight: 300;
      color: #00303b;
    }
    &.today-header {
      text-align: left;
      padding: 20px;
      span {
        display: block;
      }
      .today-span {
        font-size: 24px;
        font-weight: 500;
        color: #000000;
      }
      .date-span {
        margin-top: 14px;
        font-size: 19px;
      }
    }
  }
  .fc-head-container {
    border-top-width: 0;
  }
  th.fc-axis {
    border-width: 0;
  }
  .fc-monthView-button {
    border: 0;
    width: 45px;
    height: 45px;
    background-color: ${({ theme }) => theme.color.primary.dark};
    border-radius: 50%;
    .calendar-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  }
  .fc-toolbar > * > :not(:first-child) {
    margin-left: 0;
    margin-right: 0.75em;
  }
  .fc-event-accelerators {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    align-self: flex-end;
    gap: 8px;
    margin-top: auto;
    margin-left: auto;
    flex-shrink: 0;
    width: auto;
    max-width: 100%;
    box-sizing: border-box;
  }
  .fc-event-accel-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 6px;
    position: relative;
    flex-shrink: 0;
    min-width: 36px;
    min-height: 36px;
    box-sizing: border-box;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }
  .fc-event-accel-btn:hover {
    filter: brightness(0.97);
  }
  .fc-accel-pending-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    font-size: 18px !important;
    line-height: 1 !important;
    color: #c62828 !important;
    z-index: 1;
    pointer-events: none;
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.9));
  }
  .fc-event-accel-icon {
    font-size: 26px !important;
    line-height: 1 !important;
  }
  /* Mesmo recorte do badge de pendência (canto sup. direito do botão). */
  .fc-accel-check {
    position: absolute;
    top: -2px;
    right: -2px;
    font-size: 18px !important;
    line-height: 1 !important;
    color: #2e7d32 !important;
    z-index: 1;
    pointer-events: none;
    margin: 0 !important;
    filter: drop-shadow(0 0 1px rgba(255, 255, 255, 0.9));
  }
  .fc-event-accel-more {
    position: relative;
    flex-shrink: 0;
  }
  .fc-event-accel-more-menu {
    display: none;
    position: absolute;
    right: 0;
    bottom: calc(100% + 4px);
    min-width: 188px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(0, 48, 59, 0.12);
    padding: 4px 0;
    z-index: 30;
  }
  .fc-event-accel-more--open .fc-event-accel-more-menu {
    display: block;
  }
  .fc-event-accel-more-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    cursor: pointer;
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
  }
  .fc-event-accel-more-item:hover {
    background: rgba(0, 48, 59, 0.06);
  }
  .fc-event-accel-more-item .material-icons {
    font-size: 22px !important;
    line-height: 1 !important;
  }
`;

const MonthCalendar = props => {
  const jss = create({
    ...jssPreset(),
    insertionPoint: document.getElementById("jss-calendar-mui-style")
  });
  return (
    <StylesProvider jss={jss}>
      <StyledThemeProvider theme={StyledTheme}>
        <MultipleMonthCalendar {...props} />
      </StyledThemeProvider>
    </StylesProvider>
  );
};

class Calendar extends PureComponent {
  static setIconButton() {
    const calendarIcon = document.createElement("span");
    calendarIcon.textContent = "date_range";
    calendarIcon.className = "material-icons";
    const calendarButton = document.querySelector(
      ".fc-monthView-button .material-icons"
    );
    if (calendarButton) {
      calendarButton.className = "calendar-icon";
      calendarButton.appendChild(calendarIcon);
    }

    const leftButtons = Array.from(
      document.querySelectorAll(
        ".fc-prev-button span, .fc-prevButton-button span"
      )
    );
    leftButtons.forEach(btn => {
      btn.textContent = "chevron_left"; // eslint-disable-line no-param-reassign
      btn.className = "material-icons"; // eslint-disable-line no-param-reassign
    });

    const rightButtons = Array.from(
      document.querySelectorAll(
        ".fc-next-button span, .fc-nextButton-button span"
      )
    );
    rightButtons.forEach(btn => {
      btn.textContent = "chevron_right"; // eslint-disable-line no-param-reassign
      btn.className = "material-icons"; // eslint-disable-line no-param-reassign
    });
  }

  selectedMonth = moment();

  calendarRef = React.createRef();

  view = "week";

  constructor() {
    super();
    this.state = {
      loading: true
    };
  }

  componentDidMount() {
    const { options, today } = this.props;

    const calendarApi = this.calendarRef.current.getApi();
    calendarApi.render();

    this.scrollHour();
    if (today) {
      // Format the column header for today
      this.formatTodayHeader();
    } else if (options.header !== false) {
      // Will render the header
      Calendar.setIconButton();

      const monthDiv = document.createElement("div");
      monthDiv.className = "month-calendar-container";
      monthDiv.id = "month-calendar-container";

      const containerEl = document.querySelector(".fc-view-container");
      containerEl.parentNode.insertBefore(monthDiv, containerEl);
      this.renderMonthView(monthDiv);
    }
  }

  componentDidUpdate(prevProps) {
    const { options } = this.props;
    this.scrollHour();
    Calendar.setIconButton();

    // options.height: FullCalendar; shape not fully listed in propTypes
    // eslint-disable-next-line react/prop-types
    const prevH = prevProps.options && prevProps.options.height;
    // eslint-disable-next-line react/prop-types
    const nextH = options && options.height;
    if (this.calendarRef.current && prevH !== nextH) {
      requestAnimationFrame(() => {
        if (this.calendarRef.current) {
          this.calendarRef.current.getApi().updateSize();
        }
      });
    }

    const monthDiv = document.querySelector(".month-calendar-container");
    if (monthDiv) {
      const containerEl = document.querySelector(".fc-view-container");
      if (containerEl && containerEl.parentNode) {
        containerEl.parentNode.insertBefore(monthDiv, containerEl);
        this.renderMonthView(monthDiv);
      }
    }
  }

  onSelectDay = selectedDay => {
    const calendarApi = this.calendarRef.current.getApi();
    calendarApi.gotoDate(selectedDay.toDate());
    this.changeMonthView();
    Calendar.setIconButton();
  };

  getCustomButtonsOpt() {
    return {
      prevButton: {
        text: "",
        icon: "chevron-left",
        click: () => {
          if (this.view === "month") {
            this.selectedMonth = moment(this.selectedMonth).subtract(1, "M");
            this.reRenderMonthView();
          } else {
            this.selectedMonth = moment(this.selectedMonth).subtract(7, "d");
            const calendarApi = this.calendarRef.current.getApi();
            calendarApi.gotoDate(this.selectedMonth.toDate());
            calendarApi.render();
            Calendar.setIconButton();
          }
        }
      },
      nextButton: {
        text: "",
        icon: "chevron-right",
        click: () => {
          if (this.view === "month") {
            this.selectedMonth = moment(this.selectedMonth).add(1, "M");
            this.reRenderMonthView();
          } else {
            this.selectedMonth = moment(this.selectedMonth).add(7, "d");
            const calendarApi = this.calendarRef.current.getApi();
            calendarApi.gotoDate(this.selectedMonth.toDate());
            calendarApi.render();
            Calendar.setIconButton();
          }
        }
      },
      monthView: {
        text: "date_range",
        icon: " material-icons",
        click: () => this.changeMonthView()
      }
    };
  }

  formatTodayHeader = () => {
    const { t } = this.props;
    const viewContainer = document.querySelector(".fc-view-container");
    viewContainer.classList.add("today-container");
    const spaceHeader = document.querySelector(".fc-axis.fc-widget-header");
    spaceHeader.remove();
    const todayHeader = document.querySelector(".fc-day-header.fc-today");
    todayHeader.classList.add("today-header");
    const dateSpan = todayHeader.querySelector("span");
    dateSpan.textContent =
      dateSpan.textContent.charAt(0).toUpperCase() +
      dateSpan.textContent.slice(1);
    dateSpan.classList.add("date-span");
    const todaySpan = document.createElement("span");
    todaySpan.classList.add("today-span");
    todaySpan.textContent = t("ui.calendar.today");
    todayHeader.prepend(todaySpan);
  };

  scrollHour = () => {
    const time = moment();
    const roundedTime = time.startOf("hour");
    const scrollContainer = document.querySelector(
      ".fc-widget-content .fc-scroller"
    );
    const hourToScroll = document.querySelector(
      `tr[data-time="${roundedTime.format("HH:mm:ss")}"]`
    );
    scrollContainer.scrollTop = hourToScroll.offsetTop;
  };

  /**
   * Atalhos no card (modo demo): mesmos ícones das ações da turma.
   * Frequência: pendente (ícone error vermelho) ou realizada (check verde + tooltip).
   */
  appendScheduleAccelerators = (infos, minutes, ext) => {
    if (!isClassDiaryDemo()) return;
    const { classId, slotKey } = ext;
    if (!classId || !slotKey || minutes < 45) return;
    const { t } = this.props;

    const titleElement = infos.el.querySelector(".fc-title");
    if (!titleElement) return;

    const status = getDemoScheduleSlotStatus(slotKey);
    const q = encodeURIComponent(slotKey);

    const row = document.createElement("div");
    row.className = "fc-event-accelerators";

    const accent = ext.iconColor || "#1a3a45";

    const mkBtn = (iconName, label, path, kind) => {
      const wrap = document.createElement("span");
      wrap.className = "fc-event-accel-btn";
      const needsAction = kind === "frequency";
      const isDone = needsAction && status.frequencyDone;

      if (needsAction && isDone) wrap.classList.add("realizado");

      let titleText = label;
      if (needsAction) {
        titleText = isDone
          ? t("ui.calendar.done")
          : t("ui.calendar.pendingSuffix", { label });
      }
      wrap.title = titleText;

      wrap.addEventListener("click", e => {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(`/classes/${classId}${path}?demoSlotKey=${q}`);
      });

      if (needsAction && !isDone) {
        const badge = document.createElement("span");
        badge.className = "material-icons fc-accel-pending-badge";
        badge.setAttribute("aria-hidden", "true");
        badge.textContent = "error";
        wrap.appendChild(badge);
      }

      const icon = document.createElement("span");
      icon.className = "material-icons fc-event-accel-icon";
      icon.textContent = iconName;
      icon.style.color = accent;
      wrap.appendChild(icon);

      if (needsAction && isDone) {
        const check = document.createElement("span");
        check.className = "material-icons fc-accel-check";
        check.textContent = "check_circle";
        wrap.appendChild(check);
      }

      styleAccelButton(
        wrap,
        accent,
        needsAction && isDone ? "realizado" : "default"
      );
      return wrap;
    };

    row.appendChild(
      mkBtn(
        "playlist_add_check",
        t("ui.calendar.frequency"),
        "/frequency",
        "frequency"
      )
    );

    const moreWrap = document.createElement("span");
    moreWrap.className = "fc-event-accel-more";

    const moreBtn = document.createElement("span");
    moreBtn.className = "fc-event-accel-btn fc-event-accel-more-btn";
    moreBtn.title = t("common.messages.moreActions");
    moreBtn.setAttribute("role", "button");
    moreBtn.setAttribute("aria-haspopup", "true");
    moreBtn.setAttribute("aria-expanded", "false");
    const moreIcon = document.createElement("span");
    moreIcon.className = "material-icons fc-event-accel-icon";
    moreIcon.textContent = "more_horiz";
    moreIcon.style.color = accent;
    moreBtn.appendChild(moreIcon);
    styleAccelButton(moreBtn, accent, "default");

    const menu = document.createElement("div");
    menu.className = "fc-event-accel-more-menu";
    menu.setAttribute("role", "menu");

    const mkMenuItem = (iconName, label, path) => {
      const item = document.createElement("div");
      item.className = "fc-event-accel-more-item";
      item.setAttribute("role", "menuitem");
      const ic = document.createElement("span");
      ic.className = "material-icons";
      ic.textContent = iconName;
      ic.style.color = accent;
      const tx = document.createElement("span");
      tx.textContent = label;
      tx.style.color = "#1a3a45";
      item.appendChild(ic);
      item.appendChild(tx);
      item.addEventListener("click", e => {
        e.stopPropagation();
        e.preventDefault();
        moreWrap.classList.remove("fc-event-accel-more--open");
        moreBtn.setAttribute("aria-expanded", "false");
        removeAccelMoreOutsideHandler(moreWrap);
        window.location.assign(`/classes/${classId}${path}?demoSlotKey=${q}`);
      });
      return item;
    };

    menu.appendChild(
      mkMenuItem("description", t("classes.actions.gradesV2"), "/gradesV2")
    );
    menu.appendChild(
      mkMenuItem("assignment", t("ui.calendar.occurrences"), "/occurrences")
    );

    moreBtn.addEventListener("click", e => {
      e.stopPropagation();
      e.preventDefault();
      const wasOpen = moreWrap.classList.contains("fc-event-accel-more--open");
      document
        .querySelectorAll(".fc-event-accel-more--open")
        .forEach(wrapEl => {
          wrapEl.classList.remove("fc-event-accel-more--open");
          const btn = wrapEl.querySelector(".fc-event-accel-more-btn");
          if (btn) btn.setAttribute("aria-expanded", "false");
          removeAccelMoreOutsideHandler(wrapEl);
        });
      const willOpen = !wasOpen;
      if (willOpen) {
        moreWrap.classList.add("fc-event-accel-more--open");
        moreBtn.setAttribute("aria-expanded", "true");
        const outsideClose = ev => {
          if (!moreWrap.contains(ev.target)) {
            moreWrap.classList.remove("fc-event-accel-more--open");
            moreBtn.setAttribute("aria-expanded", "false");
            removeAccelMoreOutsideHandler(moreWrap);
          }
        };
        accelMoreOutsideHandlers.set(moreWrap, outsideClose);
        setTimeout(() => {
          document.addEventListener("click", outsideClose, true);
        }, 0);
      } else {
        moreBtn.setAttribute("aria-expanded", "false");
      }
    });

    moreWrap.appendChild(moreBtn);
    moreWrap.appendChild(menu);
    row.appendChild(moreWrap);

    titleElement.appendChild(row);
  };

  eventRender = infos => {
    // Uma só camada: preenchimento + borda esquerda (evita div.fc-bg.fc-border absoluta sobre a linha da grade)
    const eventCard = infos.el;
    const cardColor = eventCard.style.backgroundColor;
    const ext = infos.event.extendedProps || {};
    const accentColor = ext.iconColor || cardColor;
    eventCard.style.backgroundColor = "white";
    const coloredDiv = document.createElement("div");
    coloredDiv.style.backgroundColor = cardColor;
    coloredDiv.style.borderLeft = `3px solid ${accentColor}`;
    coloredDiv.style.boxSizing = "border-box";
    coloredDiv.classList.add("fc-bg");

    eventCard.prepend(coloredDiv);

    // Adicionando classes de tempo
    const end = moment(infos.event.end);
    const start = moment(infos.event.start);
    const duration = moment.duration(end.diff(start));
    const minutes = duration.asMinutes();

    let minuteClass;
    if (minutes <= 10) {
      minuteClass = "min-10";
    } else if (minutes <= 20) {
      minuteClass = "min-20";
    } else if (minutes <= 30) {
      minuteClass = "min-30";
    } else if (minutes <= 45) {
      minuteClass = "min-45";
    } else {
      minuteClass = "min-long";
    }
    eventCard.classList.add(minuteClass);

    const contentEl = infos.el.querySelector(".fc-content");
    if (contentEl) {
      contentEl.style.setProperty("display", "flex", "important");
      contentEl.style.setProperty("flex-direction", "column", "important");
      contentEl.style.setProperty("align-items", "stretch", "important");
      contentEl.style.setProperty("justify-content", "flex-start", "important");
      contentEl.style.setProperty("width", "100%", "important");
      contentEl.style.setProperty("box-sizing", "border-box", "important");
    }

    // Tratando conteudo do card
    const titleElement = infos.el.querySelector(".fc-title");
    if (!titleElement) {
      return;
    }
    titleElement.style.setProperty("width", "100%", "important");
    titleElement.style.setProperty("max-width", "100%", "important");
    titleElement.style.setProperty("min-width", "0", "important");
    titleElement.style.setProperty("box-sizing", "border-box", "important");
    while (titleElement.firstChild) {
      titleElement.removeChild(titleElement.firstChild);
    }

    const { title } = infos.event;
    const { description = "", icon, iconColor } = ext;

    const titleNode = document.createElement("h1");
    titleNode.appendChild(document.createTextNode(title));

    const descNode = document.createElement("h2");
    descNode.appendChild(document.createTextNode(description));

    /* Sempre a mesma estrutura (linha ícone + texto). Ícone fallback evita ramo “só h1/h2”
       quando extendedProps falha ou glifo não existe na fonte. */
    const row = document.createElement("div");
    row.className = "fc-event-title-row";

    const iconEl = document.createElement("span");
    iconEl.className = "fc-event-icon material-icons";
    iconEl.textContent = icon || "event";
    iconEl.style.color = iconColor || "#1a3a45";

    const textCol = document.createElement("div");
    textCol.className = "fc-event-title-text";
    textCol.appendChild(titleNode);
    textCol.appendChild(descNode);

    row.appendChild(iconEl);
    row.appendChild(textCol);
    titleElement.appendChild(row);
    this.appendScheduleAccelerators(infos, minutes, ext);
  };

  changeMonthView() {
    const monthView = document.querySelector("#month-calendar-container");
    monthView.style.height = monthView.style.height === "auto" ? "0" : "auto";
    this.view = this.view === "week" ? "month" : "week";
  }

  reRenderMonthView() {
    const monthDiv = document.querySelector(".month-calendar-container");
    if (monthDiv) {
      const containerEl = document.querySelector(".fc-view-container");
      containerEl.parentNode.insertBefore(monthDiv, containerEl);
      this.renderMonthView(monthDiv);
    }
  }

  renderMonthView(container) {
    const root = createRoot(container);
    root.render(
      <MonthCalendar
        selectedMonth={this.selectedMonth}
        onSelectDate={this.onSelectDay}
      />
    );
  }

  render() {
    const { options, today, fillContainer } = this.props;
    const { loading } = this.state;

    return (
      <CalendarStyle today={today} $fillContainer={!!fillContainer}>
        {loading && (
          <Dialog open={loading}>
            <DialogContent>
              <CircularProgress />
            </DialogContent>
          </Dialog>
        )}
        <FullCalendar
          ref={this.calendarRef}
          timeZone="UTC"
          plugins={[timeGridPlugin, dayGridPlugin]}
          locale={ptBrLocale}
          eventRender={this.eventRender}
          loading={e => this.setState({ loading: e })}
          customButtons={this.getCustomButtonsOpt()}
          scrollTime={moment()
            .startOf("hour")
            .format("HH:mm:ss")}
          header={
            today
              ? false
              : {
                  left: "title prevButton,nextButton",
                  right: "monthView"
                }
          }
          {...options}
          now={localClockAsUtc}
          nowIndicator
        />
      </CalendarStyle>
    );
  }
}

Calendar.defaultProps = {
  today: false,
  fillContainer: false
};

Calendar.propTypes = {
  options: PropTypes.shape({
    ...FullCalendar.propTypes,
    header: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
  }).isRequired,
  today: PropTypes.bool,
  fillContainer: PropTypes.bool,
  t: PropTypes.func.isRequired
};

export default withTranslation()(Calendar);
